import { useNavigate } from "react-router-dom";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, ResponsiveContainer } from "recharts";
import { ArrowRight } from "lucide-react";
import { CATEGORIES } from "@/lib/data";
import { getResults } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { levelText } from "@/lib/scoring";

export default function Score() {
  const nav = useNavigate();
  const { user } = useAuth();
  const r = getResults();

  if (!r) {
    return (
      <div className="text-center py-24" data-testid="no-results">
        <p className="text-neutral-400">Henüz test sonucun yok.</p>
        <button onClick={() => nav("/test")} className="btn-primary mt-6">Testi Başlat</button>
      </div>
    );
  }

  const radarData = CATEGORIES.map((c) => ({ subject: c.label, value: r.scores[c.key] }));
  const strongest = [...CATEGORIES].map(c => ({ ...c, score: r.scores[c.key] })).sort((a, b) => b.score - a.score).slice(0, 3);
  const dev = [...CATEGORIES].map(c => ({ ...c, score: r.scores[c.key] })).sort((a, b) => a.score - b.score).slice(0, 3);

  return (
    <div data-testid="score-page">
      <h1 className="text-5xl md:text-6xl font-semibold tracking-tight h-gradient">
        {user?.first_name ? `${user.first_name}, Life 101 profilin hazır.` : "Senin Life 101 profilin hazır."}
      </h1>
      <p className="mt-5 text-neutral-400 max-w-2xl">
        Cevaplarına göre 10 temel yaşam alanındaki mevcut durumun aşağıda görünüyor.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-12">
        <div className="lg:col-span-4 glass p-7 flex flex-col justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.15em] text-neutral-500">Genel Skor</div>
            <div className="mt-3 text-7xl font-bold h-gradient leading-none" data-testid="general-score">
              {r.general}<span className="text-2xl text-neutral-500 align-top ml-1">/100</span>
            </div>
            <div className="mt-4 flex items-center gap-2 text-neutral-400 text-sm">
              <span className="w-2 h-2 rounded-full bg-white shadow-[0_0_8px_rgba(255,255,255,0.8)]" />
              {levelText(r.general)}
            </div>
          </div>
        </div>

        <div className="lg:col-span-5 glass p-6">
          <ResponsiveContainer width="100%" height={360}>
            <RadarChart data={radarData} outerRadius="78%">
              <PolarGrid stroke="#1f1f1f" />
              <PolarAngleAxis dataKey="subject" tick={{ fill: "#a3a3a3", fontSize: 11 }} />
              <Radar dataKey="value" stroke="#ffffff" fill="rgba(255,255,255,0.15)" strokeWidth={1.5} dot={{ fill: "#fff", r: 3 }} />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        <div className="lg:col-span-3 space-y-4">
          <div className="glass p-5">
            <div className="text-xs uppercase tracking-[0.15em] text-neutral-500 mb-4">En Güçlü 3 Alanın</div>
            <div className="space-y-3">
              {strongest.map((s) => (
                <div key={s.key}>
                  <div className="flex justify-between text-sm mb-1.5"><span className="text-neutral-200">{s.label}</span><span className="text-neutral-400">{s.score}/100</span></div>
                  <div className="score-bar"><div style={{ width: `${s.score}%` }} /></div>
                </div>
              ))}
            </div>
          </div>
          <div className="glass p-5">
            <div className="text-xs uppercase tracking-[0.15em] text-neutral-500 mb-4">Gelişime Açık 3 Alanın</div>
            <div className="space-y-3">
              {dev.map((s) => (
                <div key={s.key}>
                  <div className="flex justify-between text-sm mb-1.5"><span className="text-neutral-200">{s.label}</span><span className="text-neutral-400">{s.score}/100</span></div>
                  <div className="score-bar"><div style={{ width: `${s.score}%` }} /></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="glass mt-6 p-6">
        <div className="text-xs uppercase tracking-[0.15em] text-neutral-500 mb-5">Alanlara Göre Skorların</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {CATEGORIES.map((c) => (
            <div key={c.key}>
              <div className="flex justify-between text-sm mb-1.5"><span className="text-neutral-200">{c.label}</span><span className="text-neutral-400">{r.scores[c.key]}/100</span></div>
              <div className="score-bar"><div style={{ width: `${r.scores[c.key]}%` }} /></div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-10 flex justify-end">
        <button onClick={() => nav("/rapor")} className="btn-primary inline-flex items-center gap-2" data-testid="view-report-btn">
          Detaylı Raporu Gör <ArrowRight size={16} />
        </button>
      </div>

      <div className="glass mt-6 p-6 flex flex-wrap items-center justify-between gap-4">
        <div className="text-sm text-neutral-300">Sonucunu paylaş:</div>
        <div className="flex flex-wrap gap-3">
          <a target="_blank" rel="noreferrer" href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Life 101 Yaşam Skorum ${r.general}/100 çıktı. Sen de dene:`)}&url=${encodeURIComponent(window.location.origin)}`} className="btn-secondary text-sm" data-testid="share-twitter">X / Twitter</a>
          <a target="_blank" rel="noreferrer" href={`https://wa.me/?text=${encodeURIComponent(`Life 101 Yaşam Skorum ${r.general}/100. Sen de ölç: ${window.location.origin}`)}`} className="btn-secondary text-sm" data-testid="share-whatsapp">WhatsApp</a>
          <a target="_blank" rel="noreferrer" href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.origin)}`} className="btn-secondary text-sm" data-testid="share-linkedin">LinkedIn</a>
          <button onClick={() => { navigator.clipboard.writeText(window.location.origin); }} className="btn-secondary text-sm" data-testid="share-copy">Linki Kopyala</button>
        </div>
      </div>
    </div>
  );
}
